// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline component for managing asset project assignments.
 * This module provides an interactive timeline interface for
 * viewing and managing an asset's project assignments over time.
 * It supports assignment creation, modification, and visualization
 * with tooltips and scrolling capabilities.
 *
 * Key features:
 * - Project assignment timeline
 * - Interactive assignment creation
 * - Assignment management
 * - Timeline navigation
 * - Tooltip information
 * - Responsive layout
 */
import {
  AssetProjectHistorySegment,
  getAssetProjectHistory,
  getAssetProjectHistorySegment,
} from '@/asset/api/get-asset-project-history';
import { Asset } from '@/asset/api/types';
import { AssetAssignmentDialog } from '@/asset/components/asset-assignment-dialog';
import { AssetAssignmentTimelineSegment } from '@/asset/components/asset-assignment-timeline-segment';
import { AssetAssignmentTimelineTooltip } from '@/asset/components/asset-assignment-timeline-tooltip';
import { AssetUnassignmentDialog } from '@/asset/components/asset-unassignment-dialog';
import { CreateAssetAssignmentDialog } from '@/asset/components/create-asset-assignment-dialog';
import { UpdateAssetAssignmentDialog } from '@/asset/components/update-asset-assignment-dialog';
import { ScrollIndicator } from '@/components/scroll-indicator';
import { TimelineAxis } from '@/components/timeline-axis';
import { TimelineColumns } from '@/components/timeline-columns';
import { TimelineCurrentDateIndicator } from '@/components/timeline-current-date-indicator';
import { TimelineDensity } from '@/constants/timeline-density';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { localPoint } from '@visx/event';
import { scaleTime } from '@visx/scale';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { omit } from 'lodash-es';
import { DateTime, Interval } from 'luxon';
import { MouseEvent, MouseEventHandler, useRef, useState } from 'react';

const MINIMUM_ROW_COUNT = 2;
const MAXIMUM_ROW_COUNT = 2;
const AXIS_HEIGHT = 40;

/**
 * Props for configuring the AssetProjectTimeline component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset whose project timeline is being displayed.
   * Contains project assignment information.
   */
  asset: Asset;

  /**
   * The zoom level for the timeline display.
   * Controls the time scale granularity.
   */
  zoom: TimelineDensity;

  /**
   * Time interval to display in the timeline.
   * Determines the visible time range.
   */
  interval: Interval;

  /**
   * Height of each timeline bar in pixels.
   * Controls the vertical size of segments.
   */
  barHeight: number;

  /**
   * Margin between timeline bars in pixels.
   * Controls vertical spacing of segments.
   */
  barMargin: number;

  /**
   * If true, the timeline actions are disabled.
   * Prevents assignment changes when true.
   *
   * @default false
   */
  isDisabled?: boolean;
}

/**
 * A timeline component for asset project management.
 * Provides an interactive interface for viewing and managing
 * project assignments over time, with support for creating
 * new assignments, modifying existing ones, and visualizing
 * the project history.
 *
 * Features:
 * - Project assignment timeline
 * - Interactive assignment creation
 * - Assignment modification
 * - Timeline navigation
 * - Tooltip information
 * - Scroll management
 * - Responsive layout
 *
 * Usage example:
 * ```tsx
 * function AssetProjects({ asset }: Props) {
 *   const interval = Interval.fromDateTimes(
 *     DateTime.now().minus({ months: 6 }),
 *     DateTime.now()
 *   );
 *
 *   return (
 *     <AssetProjectTimeline
 *       asset={asset}
 *       zoom="week"
 *       interval={interval}
 *       barHeight={30}
 *       barMargin={8}
 *     />
 *   );
 * }
 * ```
 */
export function AssetProjectTimeline(props: Props) {
  const theme = useTheme();
  const svgRef = useRef<SVGElement | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const resizeObserver = useResizeObserver(boxRef);
  const scrollIndicator = useScrollIndicator();
  const [input, setInput] = useState<DateTime>();
  const navigate = useNavigate();
  const location = useLocation();
  const search = location.search;
  const pathname = location.pathname;
  const unassignmentDialog = useDisclosure();
  const assignmentDialog = useDisclosure(search.assign !== undefined);
  const changeAssignmentDialog = useDisclosure();
  const { data } = useQuery({
    queryKey: ['asset', 'linked-project-assignment'],
    queryFn: () =>
      getAssetProjectHistorySegment(
        props.asset.key,
        search.unassign ?? ''
      ).then((segment) => {
        changeAssignmentDialog.open();
        return segment;
      }),
    enabled: search.unassign !== undefined,
  });

  const {
    tooltipTop,
    tooltipLeft,
    tooltipOpen,
    tooltipData,
    showTooltip,
    hideTooltip,
  } = useTooltip<AssetProjectHistorySegment>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const assignmentQuery = useQuery({
    queryKey: ['asset', props.asset.key, 'project-history'],
    queryFn: () => getAssetProjectHistory(props.asset.key),
  });

  const dateScale = scaleTime({
    range: [40, resizeObserver.width - 40],
    domain: [props.interval.start.toJSDate(), props.interval.end.toJSDate()],
  });

  const projects =
    assignmentQuery.data?.history.filter((assignment) => {
      if (assignment.end === null) {
        return assignment.start < props.interval.end;
      }

      const interval = assignment.start.until(assignment.end);

      return props.interval.overlaps(interval);
    }) ?? [];

  const rowHeight = props.barHeight + props.barMargin * 2;
  const mininumHeight = rowHeight * MINIMUM_ROW_COUNT;
  const maximumHeight = rowHeight * MAXIMUM_ROW_COUNT;
  const height = Math.max(rowHeight * projects.length, mininumHeight);
  const isDialogOpen = input !== undefined;
  const today = DateTime.now().endOf('day');
  const clickTargetWidth = dateScale(today.toJSDate()) ?? 0;

  /**
   * Calculates the vertical position for a timeline row.
   *
   * @param index - The row index
   * @returns The Y coordinate in pixels
   */
  const getY = (index: number) => {
    return index * rowHeight + props.barMargin;
  };

  /**
   * Converts an X coordinate to a DateTime.
   *
   * @param x - The X coordinate in pixels
   * @returns The corresponding DateTime
   */
  const getDateTime = (x: number) => {
    const date = dateScale.invert(x);
    return DateTime.fromJSDate(date);
  };

  /**
   * Gets the local point from a mouse event.
   *
   * @param event - The mouse event
   * @returns The local point or null
   */
  const getPoint = (event: MouseEvent<SVGElement>) => {
    if (svgRef.current === null) {
      return null;
    }

    return localPoint(svgRef.current, event);
  };

  /**
   * Handles dialog close events.
   * Resets input state.
   */
  const handleCloseDialog = () => {
    setInput(undefined);
  };

  /**
   * Handles scroll indicator clicks.
   * Scrolls to show more assignments.
   */
  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  /**
   * Creates a mouse move handler for a project segment.
   * Shows tooltip with project information.
   */
  const createMouseMoveHandler =
    (project: AssetProjectHistorySegment) =>
    (event: MouseEvent<SVGElement>) => {
      const point = getPoint(event);

      if (point === null) {
        return;
      }

      showTooltip({
        tooltipTop: point.y,
        tooltipLeft: point.x,
        tooltipData: project,
      });
    };

  /**
   * Handles mouse leave events.
   * Hides the tooltip.
   */
  const handleMouseLeave = () => {
    hideTooltip();
  };

  /**
   * Handles assignment button clicks.
   * Opens appropriate dialog based on current state.
   */
  const handleClickAssignment = () => {
    if (props.asset.project === null) {
      return assignmentDialog.open();
    }

    unassignmentDialog.open();
  };

  /**
   * Handles container reference assignment.
   * Combines SVG and tooltip portal refs.
   */
  const handleContainerRef = (element: SVGElement | null) => {
    svgRef.current = element;
    containerRef(element);
  };

  /**
   * Handles timeline click events.
   * Sets input date for new assignment.
   */
  const handleClick: MouseEventHandler<SVGElement> = (event) => {
    const point = getPoint(event);

    if (point === null) {
      return;
    }

    setInput(getDateTime(point.x));
  };

  return (
    <Box ref={boxRef} position="relative">
      <CreateAssetAssignmentDialog
        asset={props.asset}
        isOpen={isDialogOpen}
        defaultStartDate={input}
        onClose={handleCloseDialog}
      />
      <AssetAssignmentDialog
        key={`assignment-${props.asset.project?.key}`}
        asset={props.asset}
        isOpen={assignmentDialog.isOpen}
        onClose={() => {
          navigate({ to: pathname, search: omit(search, 'assign') });
          assignmentDialog.close();
        }}
      />
      <AssetUnassignmentDialog
        key={`unassignment-${props.asset.project?.key}`}
        asset={props.asset}
        isOpen={unassignmentDialog.isOpen}
        onClose={unassignmentDialog.close}
      />
      {data && (
        <UpdateAssetAssignmentDialog
          asset={props.asset}
          assignment={data.segment}
          isOpen={changeAssignmentDialog.isOpen}
          onClose={() => {
            navigate({ to: pathname, search: omit(search, 'unassign') });
            changeAssignmentDialog.close();
          }}
        />
      )}
      <Stack
        paddingX={2}
        paddingY={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack>
          <Typography variant="subtitle2">Project assignment</Typography>
          <Typography variant="body2" color="text.secondary">
            Commercial history for this asset
          </Typography>
        </Stack>
        <Button
          color="secondary"
          variant="contained"
          onClick={handleClickAssignment}
          disabled={props.isDisabled}
        >
          {props.asset.project === null ? 'Assign' : 'Unassign'}
        </Button>
      </Stack>
      <svg width="100%" height={AXIS_HEIGHT} style={{ display: 'block' }}>
        <TimelineAxis
          scale={dateScale}
          width={resizeObserver.width}
          density={props.zoom}
          interval={props.interval}
          axisHeight={AXIS_HEIGHT}
          chartHeight={AXIS_HEIGHT}
        />
      </svg>
      <Box
        ref={scrollIndicator.ref}
        minHeight={mininumHeight}
        maxHeight={maximumHeight}
        overflow="auto"
      >
        <ScrollIndicator
          label="More assignments"
          isVisible={scrollIndicator.isVisible}
          onClick={handleClickScroll}
        />
        <svg
          ref={handleContainerRef}
          width="100%"
          height={height}
          style={{ display: 'block' }}
        >
          <TimelineProvider>
            {tooltipOpen && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  padding: 0,
                  zIndex: theme.zIndex.tooltip,
                  color: theme.palette.text.primary,
                }}
              >
                {tooltipData && (
                  <AssetAssignmentTimelineTooltip
                    serial={props.asset.serial}
                    segment={tooltipData}
                  />
                )}
              </TooltipInPortal>
            )}
            <TimelineColumns
              y={0}
              scale={dateScale}
              height={height}
              density={props.zoom}
              interval={props.interval}
            />
            <rect
              x={0}
              y={0}
              fill="transparent"
              height={height}
              width={clickTargetWidth}
              onClick={handleClick}
            />
            <TimelineCurrentDateIndicator
              y={0}
              height={height}
              scale={dateScale}
            />
            {projects.map((segment, index) => {
              return (
                <AssetAssignmentTimelineSegment
                  isEditable
                  key={segment.id}
                  y={getY(index)}
                  scale={dateScale}
                  asset={props.asset}
                  height={props.barHeight}
                  segment={segment}
                  onMouseMove={createMouseMoveHandler(segment)}
                  onMouseLeave={handleMouseLeave}
                />
              );
            })}
          </TimelineProvider>
        </svg>
      </Box>
    </Box>
  );
}
